import * as React from "react";
export function BooleanToggleField({ value, onChange, }) {
    return (React.createElement("button", { type: "button", role: "switch", "aria-checked": value, className: `facet-toggle ${value ? "is-on" : "is-off"}`, onClick: () => onChange(!value) },
        React.createElement("span", { className: "facet-toggle-track" },
            React.createElement("span", { className: "facet-toggle-thumb" }))));
}
export function NumberField({ value, onCommit, }) {
    const [draft, setDraft] = React.useState(String(value));
    React.useEffect(() => {
        setDraft(String(value));
    }, [value]);
    const commit = React.useCallback(() => {
        const parsed = Number(draft);
        if (!Number.isFinite(parsed)) {
            setDraft(String(value));
            return;
        }
        if (parsed !== value) {
            onCommit(parsed);
        }
        else {
            setDraft(String(value));
        }
    }, [draft, onCommit, value]);
    return (React.createElement("input", { className: "facet-number-input", type: "number", step: "any", value: draft, onChange: (event) => setDraft(event.target.value), onBlur: commit, onKeyDown: (event) => {
            if (event.key === "Enter") {
                commit();
            }
            else if (event.key === "Escape") {
                setDraft(String(value));
            }
        } }));
}
export function EnumField({ value, options, onChange, }) {
    return (React.createElement("select", { className: "facet-enum-select", value: String(value), onChange: (event) => onChange(Number(event.target.value)) }, options.map((option) => (React.createElement("option", { key: option.value, value: option.value }, option.label)))));
}
export function NumberTupleField({ value, length, onCommit, }) {
    return (React.createElement("div", { className: "facet-number-tuple" }, Array.from({ length }).map((_, index) => {
        var _a;
        return (React.createElement(NumberField, { key: index, value: (_a = value[index]) !== null && _a !== void 0 ? _a : 0, onCommit: (nextValue) => {
                const next = [...value];
                next[index] = nextValue;
                onCommit(next);
            } }));
    })));
}
export function TextField({ value, onCommit, }) {
    const [draft, setDraft] = React.useState(value);
    React.useEffect(() => {
        setDraft(value);
    }, [value]);
    const commit = React.useCallback(() => {
        if (draft !== value) {
            onCommit(draft);
        }
        else {
            setDraft(value);
        }
    }, [draft, onCommit, value]);
    return (React.createElement("input", { className: "facet-number-input", type: "text", value: draft, onChange: (event) => setDraft(event.target.value), onBlur: commit, onKeyDown: (event) => {
            if (event.key === "Enter") {
                commit();
            }
            else if (event.key === "Escape") {
                setDraft(value);
            }
        } }));
}
export function ColorField({ value, onCommit, }) {
    const [draft, setDraft] = React.useState(toHex(value));
    const colorInputRef = React.useRef(null);
    React.useEffect(() => {
        setDraft(toHex(value));
    }, [value]);
    const commitHex = React.useCallback((nextHex) => {
        const normalized = normalizeHex(nextHex);
        setDraft(normalized);
        const parsed = Number.parseInt(normalized.slice(1), 16);
        if (!Number.isNaN(parsed) && parsed !== value) {
            onCommit(parsed);
        }
    }, [onCommit, value]);
    return (React.createElement("div", { className: "facet-color-field" },
        React.createElement("button", { type: "button", className: "facet-color-swatch", style: { backgroundColor: draft }, onClick: () => { var _a; return (_a = colorInputRef.current) === null || _a === void 0 ? void 0 : _a.click(); }, "aria-label": "Choose color" }),
        React.createElement("input", { ref: colorInputRef, className: "facet-color-native-input", type: "color", value: draft, onChange: (event) => commitHex(event.target.value), tabIndex: -1 }),
        React.createElement("input", { className: "facet-color-hex-input", type: "text", value: draft, onChange: (event) => setDraft(event.target.value), onBlur: () => commitHex(draft), onKeyDown: (event) => {
                if (event.key === "Enter") {
                    commitHex(draft);
                }
                else if (event.key === "Escape") {
                    setDraft(toHex(value));
                }
            } })));
}
export function Number16Field({ value, onCommit, }) {
    return (React.createElement("div", { className: "facet-number-matrix" }, Array.from({ length: 4 }).map((_, row) => (React.createElement("div", { key: row, className: "facet-number-tuple" }, Array.from({ length: 4 }).map((__, col) => {
        var _a;
        const index = row * 4 + col;
        return (React.createElement(NumberField, { key: index, value: (_a = value[index]) !== null && _a !== void 0 ? _a : 0, onCommit: (nextValue) => {
                const next = [...value];
                next[index] = nextValue;
                onCommit(next);
            } }));
    }))))));
}
function toHex(value) {
    return `#${Math.max(0, Math.min(0xffffff, value)).toString(16).padStart(6, "0")}`;
}
function normalizeHex(value) {
    const cleaned = value.trim().replace(/^#/, "");
    const sliced = cleaned.slice(0, 6);
    const padded = sliced.padEnd(6, "0");
    return `#${padded}`;
}
